import { getCountryPhoneCode } from "./constants";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function capitalizeFromSnakeCase(value: string = "") {
  return value
    ?.toLowerCase() // Convert the entire string to lowercase
    .replace(/_/g, " ") // Replace underscores with spaces
    .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize each word
}

export function capitalizeFirstThenLower(input: string = "") {
  if (!input) return "";

  return `${input.charAt(0).toUpperCase()}${input.substring(1).toLowerCase()}`;
}

export function capitalizeFirst(input: string = "") {
  if (!input) return "";

  return `${input.charAt(0).toUpperCase()}${input.substring(1)}`;
}

export function capitalize(input: string = "") {
  if (!input) return "";
  const words = input.split(" ");
  const result = [];
  for (const w of words) {
    result.push(`${w.charAt(0).toUpperCase()}${w.substring(1)}`);
  }
  return result.join(" ");
}

export function truncate(input: string = "", limit: number = 15) {
  if (input.length > limit) {
    return input.substring(0, limit) + "...";
  }
  return input;
}

export function last4(input: string = "", limit: number = 4) {
  if (!input) return "";
  if (input.length > limit) {
    return input.substring(input.length - limit);
  }
  return input;
}

export function capitalizeWithTrunc(input: string = "", limit: number = 15) {
  const capitalized = capitalize(input);
  if (capitalized.length > limit) {
    return capitalized.substring(0, limit) + "...";
  }
  return capitalized;
}

export function getCurrencySymbol(countryCode: string = "USA") {
  if (!countryCode) {
    return "$"; // always return $ in any cases
  }
  let symbol = "$";
  switch (countryCode) {
    // A
    case "AFG":
      return "؋"; // Afghanistan - Afghani
    case "ALB":
      return "L"; // Albania - Lek
    case "DZA":
      return "دج"; // Algeria - Dinar
    case "AGO":
      return "Kz"; // Angola - Kwanza
    case "ARG":
      return "$"; // Argentina - Peso
    case "ARM":
      return "֏"; // Armenia - Dram
    case "AUS":
      return "A$"; // Australia - Dollar
    case "AUT":
      return "€"; // Austria - Euro
    case "AZE":
      return "₼"; // Azerbaijan - Manat
    // B
    case "BHR":
      return "BD"; // Bahrain - Dinar
    case "BGD":
      return "৳"; // Bangladesh - Taka
    case "BLR":
      return "Br"; // Belarus - Ruble
    case "BEL":
      return "€"; // Belgium - Euro
    case "BOL":
      return "Bs."; // Bolivia - Boliviano
    case "BIH":
      return "KM"; // Bosnia and Herzegovina - Mark
    case "BWA":
      return "P"; // Botswana - Pula
    case "BRA":
      return "R$"; // Brazil - Real
    case "BGR":
      return "лв"; // Bulgaria - Lev
    // C
    case "KHM":
      return "៛"; // Cambodia - Riel
    case "CMR":
      return "Fr"; // Cameroon - CFA Franc
    case "CAN":
      return "CA$"; // Canada - Dollar
    case "CHL":
      return "$"; // Chile - Peso
    case "CHN":
      return "¥"; // China - Yuan
    case "COL":
      return "$"; // Colombia - Peso
    case "CRI":
      return "₡"; // Costa Rica - Colon
    case "HRV":
      return "€"; // Croatia - Euro
    case "CUB":
      return "₱"; // Cuba - Peso
    case "CZE":
      return "Kč"; // Czech Republic - Koruna
    // D
    case "DNK":
      return "kr"; // Denmark - Krone
    case "DOM":
      return "RD$"; // Dominican Republic - Peso
    // E
    case "ECU":
      return "$"; // Ecuador - USD
    case "EGY":
      return "£"; // Egypt - Pound
    case "SLV":
      return "$"; // El Salvador - USD
    case "EST":
      return "€"; // Estonia - Euro
    case "ETH":
      return "Br"; // Ethiopia - Birr
    // F
    case "FIN":
      return "€"; // Finland - Euro
    case "FRA":
      return "€"; // France - Euro
    // G
    case "GEO":
      return "₾"; // Georgia - Lari
    case "DEU":
      return "€"; // Germany - Euro
    case "GHA":
      return "₵"; // Ghana - Cedi
    case "GRC":
      return "€"; // Greece - Euro
    case "GTM":
      return "Q"; // Guatemala - Quetzal
    // H
    case "HND":
      return "L"; // Honduras - Lempira
    case "HUN":
      return "Ft"; // Hungary - Forint
    // I
    case "ISL":
      return "kr"; // Iceland - Krona
    case "IND":
      return "₹"; // India - Rupee
    case "IDN":
      return "Rp"; // Indonesia - Rupiah
    case "IRN":
      return "﷼"; // Iran - Rial
    case "IRQ":
      return "ع.د"; // Iraq - Dinar
    case "IRL":
      return "€"; // Ireland - Euro
    case "ISR":
      return "₪"; // Israel - Shekel
    case "ITA":
      return "€"; // Italy - Euro
    case "CIV":
      return "Fr"; // Ivory Coast - CFA Franc
    // J
    case "JAM":
      return "J$"; // Jamaica - Dollar
    case "JPN":
      return "¥"; // Japan - Yen
    case "JOR":
      return "JD"; // Jordan - Dinar
    // K
    case "KAZ":
      return "₸"; // Kazakhstan - Tenge
    case "KEN":
      return "KSh"; // Kenya - Shilling
    case "KWT":
      return "KD"; // Kuwait - Dinar
    case "KGZ":
      return "с"; // Kyrgyzstan - Som
    // L
    case "LAO":
      return "₭"; // Laos - Kip
    case "LVA":
      return "€"; // Latvia - Euro
    case "LBN":
      return "ل.ل"; // Lebanon - Pound
    case "LBY":
      return "LD"; // Libya - Dinar
    case "LTU":
      return "€"; // Lithuania - Euro
    case "LUX":
      return "€"; // Luxembourg - Euro
    // M
    case "MDG":
      return "Ar"; // Madagascar - Ariary
    case "MYS":
      return "RM"; // Malaysia - Ringgit
    case "MLI":
      return "Fr"; // Mali - CFA Franc
    case "MEX":
      return "$"; // Mexico - Peso
    case "MDA":
      return "L"; // Moldova - Leu
    case "MNG":
      return "₮"; // Mongolia - Tugrik
    case "MAR":
      return "MAD"; // Morocco - Dirham
    case "MOZ":
      return "MT"; // Mozambique - Metical
    case "MMR":
      return "K"; // Myanmar - Kyat
    // N
    case "NAM":
      return "N$"; // Namibia - Dollar
    case "NPL":
      return "₨"; // Nepal - Rupee
    case "NLD":
      return "€"; // Netherlands - Euro
    case "NZL":
      return "NZ$"; // New Zealand - Dollar
    case "NIC":
      return "C$"; // Nicaragua - Cordoba
    case "NER":
      return "Fr"; // Niger - CFA Franc
    case "NGA":
      return "₦"; // Nigeria - Naira
    case "PRK":
      return "₩"; // North Korea - Won
    case "NOR":
      return "kr"; // Norway - Krone
    // O
    case "OMN":
      return "﷼"; // Oman - Rial
    // P
    case "PAK":
      return "₨"; // Pakistan - Rupee
    case "PAN":
      return "B/."; // Panama - Balboa
    case "PNG":
      return "K"; // Papua New Guinea - Kina
    case "PRY":
      return "₲"; // Paraguay - Guarani
    case "PER":
      return "S/."; // Peru - Sol
    case "PHL":
      return "₱"; // Philippines - Peso
    case "POL":
      return "zł"; // Poland - Zloty
    case "PRT":
      return "€"; // Portugal - Euro
    // Q
    case "QAT":
      return "﷼"; // Qatar - Rial
    // R
    case "ROU":
      return "lei"; // Romania - Leu
    case "RUS":
      return "₽"; // Russia - Ruble
    case "RWA":
      return "Fr"; // Rwanda - Franc
    // S
    case "SAU":
      return "﷼"; // Saudi Arabia - Rial
    case "SEN":
      return "Fr"; // Senegal - CFA Franc
    case "SRB":
      return "din"; // Serbia - Dinar
    case "SGP":
      return "S$"; // Singapore - Dollar
    case "SVK":
      return "€"; // Slovakia - Euro
    case "SVN":
      return "€"; // Slovenia - Euro
    case "SOM":
      return "Sh"; // Somalia - Shilling
    case "ZAF":
      return "R"; // South Africa - Rand
    case "KOR":
      return "₩"; // South Korea - Won
    case "SSD":
      return "£"; // South Sudan - Pound
    case "ESP":
      return "€"; // Spain - Euro
    case "LKA":
      return "₨"; // Sri Lanka - Rupee
    case "SDN":
      return "£"; // Sudan - Pound
    case "SWE":
      return "kr"; // Sweden - Krona
    case "CHE":
      return "Fr"; // Switzerland - Franc
    case "SYR":
      return "£"; // Syria - Pound
    // T
    case "TWN":
      return "NT$"; // Taiwan - Dollar
    case "TJK":
      return "SM"; // Tajikistan - Somoni
    case "TZA":
      return "Sh"; // Tanzania - Shilling
    case "THA":
      return "฿"; // Thailand - Baht
    case "TUN":
      return "DT"; // Tunisia - Dinar
    case "TUR":
      return "₺"; // Turkey - Lira
    case "TKM":
      return "T"; // Turkmenistan - Manat
    // U
    case "UGA":
      return "Sh"; // Uganda - Shilling
    case "UKR":
      return "₴"; // Ukraine - Hryvnia
    case "ARE":
      return "د.إ"; // United Arab Emirates - Dirham
    case "UK":
      return "£"; // United Kingdom - Pound
    case "USA":
      return "$"; // United States - Dollar
    case "URY":
      return "$U"; // Uruguay - Peso
    case "UZB":
      return "лв"; // Uzbekistan - Som
    // V
    case "VEN":
      return "Bs.S"; // Venezuela - Bolivar
    case "VNM":
      return "₫"; // Vietnam - Dong
    // Y
    case "YEM":
      return "﷼"; // Yemen - Rial
    // Z
    case "ZMB":
      return "ZK"; // Zambia - Kwacha
    case "ZWE":
      return "Z$"; // Zimbabwe - Dollar
    default:
      return symbol;
  }
}

/**
 * Products have current as USD, INR. This will return $ and ₹.
 *
 * Use this wherever you are getting product prices.
 * @param countryCode
 * @returns
 */
export function getProductCurrencySymbol(currencyCode?: string) {
  if (!currencyCode) {
    return "$"; // always return $ in any cases
  }
  let symbol = "$";
  switch (currencyCode) {
    case "INR":
      symbol = "₹";
      break;
    case "GBP":
      symbol = "£";
      break;
    case "JPY":
      symbol = "¥";
      break;
  }
  return symbol;
}
/**
 * Returns USD for country code USA
 * @param countryCode
 * @returns
 */
export function getCurrencyCode(countryCode: string = "USA") {
  if (!countryCode) {
    return "USD"; // always return $ in any cases
  }
  let symbol = "USD";
  switch (countryCode) {
    case "IND":
      symbol = "INR";
      break;
    case "UK":
      symbol = "GBP";
      break;
    case "JPN":
      symbol = "JPY";
      break;
  }
  return symbol;
}

export function matchPublicRoutes(pathname: string) {
  const PUBLIC_ROUTES = [
    "/",
    "/forgot-password",
    "/register",
    "/register/verify",
    "/login",
    "/receipts",
    "/pricing",
    "/refund-policy",
    "/terms",
  ];
  const HELP_REGEX = new RegExp(/^\/help\/[a-zA-Z0-9]+$/);
  return PUBLIC_ROUTES.includes(pathname) || HELP_REGEX.test(pathname);
}

export function displayPhoneNumber(
  phone?: string,
  countryCode?: string | null,
) {
  if (phone && countryCode) {
    const countryCodeToTrim = getCountryPhoneCode(countryCode);
    return phone.replace(countryCodeToTrim, "");
  }
  return "";
}

export function getRecaptchaKey() {
  return process.env.REACT_APP_RECAPTCHA_KEY ?? "";
}

export const isRecaptchaEnabled =
  process.env.REACT_APP_RECAPTCHA_ENABLED === "true";

// not sure what this does, but this came from builder.io
export function cn(...inputs: any) {
  return twMerge(clsx(inputs));
}
